import { useState } from 'react';

import { WikiCommentThread } from '@/features/wiki/types';
import { Member } from '@/features/backlog/types';
import { renderCommentBody } from '@/features/backlog/mentions';
import { timeAgo } from '@/features/backlog/format';
import MemberAvatar from '@/components/member-avatar';
import MentionInput from '@/components/mention-input';
import { Check } from '@/components/ui/icons';
import { cn } from '@/components/ui/cn';

interface Props {
  threads: WikiCommentThread[];
  members: Member[];
  meId: string;
  activeThreadId: string | null;
  pending: { id: string; quote: string } | null;
  onActivate: (id: string | null) => void;
  onReply: (threadId: string, body: string) => Promise<void> | void;
  onResolve: (threadId: string, resolved: boolean) => void;
  onDelete: (threadId: string) => void;
  onSubmitPending: (body: string) => void;
  onCancelPending: () => void;
  onClose: () => void;
}

export default function WikiCommentsRail({ threads, members, meId, activeThreadId, pending, onActivate, onReply, onResolve, onDelete, onSubmitPending, onCancelPending, onClose }: Props) {
  const byId = new Map(members.map((m) => [m.id, m]));
  const activeMembers = [...members].filter((m) => m.active).sort((a, b) => a.name.localeCompare(b.name));
  const open = threads.filter((t) => !t.resolved);
  const [showResolved, setShowResolved] = useState(false);
  const shown = showResolved ? threads : open;

  return (
    <aside className="flex w-[320px] shrink-0 flex-col border-l border-line bg-surface">
      <div className="flex items-center justify-between px-3 py-2.5">
        <span className="text-[12px] font-semibold uppercase tracking-wide text-fg-faint">Comments · {open.length}</span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setShowResolved((v) => !v)} className="rounded px-1.5 py-0.5 text-[11px] text-fg-muted hover:bg-layer hover:text-fg">{showResolved ? 'Hide resolved' : 'Show resolved'}</button>
          <button type="button" onClick={onClose} className="rounded px-1.5 py-0.5 text-[12px] text-fg-muted hover:bg-layer hover:text-fg">✕</button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {pending ? <PendingCard quote={pending.quote} activeMembers={activeMembers} onSubmit={onSubmitPending} onCancel={onCancelPending} /> : null}
        {shown.length === 0 && !pending ? (
          <div className="px-3 py-10 text-center text-[12px] text-fg-faint">Select text in the page and click "Comment" to start a thread.</div>
        ) : (
          shown.map((t) => (
            <ThreadCard
              key={t.id}
              thread={t}
              byId={byId}
              meId={meId}
              activeMembers={activeMembers}
              active={activeThreadId === t.id}
              onActivate={() => onActivate(t.id)}
              onReply={(b) => onReply(t.id, b)}
              onResolve={() => onResolve(t.id, !t.resolved)}
              onDelete={() => onDelete(t.id)}
            />
          ))
        )}
      </div>
    </aside>
  );
}

function PendingCard({ quote, activeMembers, onSubmit, onCancel }: { quote: string; activeMembers: Member[]; onSubmit: (b: string) => void; onCancel: () => void }) {
  const [draft, setDraft] = useState('');
  const send = () => {
    if (!draft.trim()) return;
    onSubmit(draft);
    setDraft('');
  };
  return (
    <div className="mb-2 rounded-xl border border-accent/60 bg-surface-raised p-2.5 ring-1 ring-accent/30">
      {quote ? <div className="mb-1.5 border-l-2 border-accent/50 pl-2 text-[11.5px] italic text-fg-faint line-clamp-2">{quote}</div> : null}
      <MentionInput value={draft} onChange={setDraft} members={activeMembers} onSubmit={send} placeholder="Comment… (@ to mention)" />
      <div className="mt-1.5 flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="text-[11.5px] font-medium text-fg-faint hover:text-fg">Cancel</button>
        <button type="button" onClick={send} disabled={!draft.trim()} className="rounded-md bg-accent px-2.5 py-1 text-[12px] font-semibold text-white disabled:opacity-40">Comment</button>
      </div>
    </div>
  );
}

function ThreadCard({ thread, byId, meId, activeMembers, active, onActivate, onReply, onResolve, onDelete }: {
  thread: WikiCommentThread;
  byId: Map<string, Member>;
  meId: string;
  activeMembers: Member[];
  active: boolean;
  onActivate: () => void;
  onReply: (b: string) => Promise<void> | void;
  onResolve: () => void;
  onDelete: () => void;
}) {
  const [draft, setDraft] = useState('');
  const send = async () => {
    if (!draft.trim()) return;
    const b = draft;
    setDraft('');
    await onReply(b);
  };
  return (
    <div
      id={`wiki-thread-${thread.id}`}
      onClick={onActivate}
      className={cn('mb-2 cursor-pointer rounded-xl border bg-surface-raised p-2.5 transition-colors', active ? 'border-accent/70 ring-1 ring-accent/40' : 'border-line hover:border-line-strong', thread.resolved && 'opacity-60')}
    >
      {thread.quote ? <div className="mb-1.5 border-l-2 border-warn/50 pl-2 text-[11.5px] italic text-fg-faint line-clamp-2">{thread.quote}</div> : null}
      {thread.comments.map((c) => {
        const a = byId.get(c.author_id ?? '');
        return (
          <div key={c.id} className="mb-1.5">
            <div className="flex items-center gap-2 text-[11px] text-fg-faint">
              <MemberAvatar member={a} size={18} className="shrink-0" />
              <span className="font-medium text-fg-soft">{c.author_name || a?.name || 'Someone'}</span>
              <span>{timeAgo(c.created_at)}</span>
            </div>
            <div className="mt-0.5 whitespace-pre-wrap pl-6 text-[12.5px] text-fg-soft">{renderCommentBody(c.body, byId, meId)}</div>
          </div>
        );
      })}
      <div className="mt-1 pl-6">
        <MentionInput value={draft} onChange={setDraft} members={activeMembers} onSubmit={send} placeholder="Reply… (@ to mention)" />
        <div className="mt-1 flex items-center justify-between">
          <div className="flex gap-2">
            <button type="button" onClick={(e) => { e.stopPropagation(); onResolve(); }} className="inline-flex items-center gap-1 text-[11.5px] font-medium text-fg-faint hover:text-emerald-500"><Check className="h-3 w-3" />{thread.resolved ? 'Reopen' : 'Resolve'}</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-[11.5px] font-medium text-fg-faint hover:text-danger">Delete</button>
          </div>
          <button type="button" onClick={(e) => { e.stopPropagation(); void send(); }} disabled={!draft.trim()} className="text-[12px] font-semibold text-accent disabled:opacity-40">Reply</button>
        </div>
      </div>
    </div>
  );
}
